import { Link, createFileRoute } from '@tanstack/react-router';
import { ImageIcon } from 'lucide-react';
import { useState } from 'react';
import { Trans } from 'react-i18next';
import { DateInput } from '@/components/date-input';
import { CurrencySettingsSection } from '@/components/currency-settings-section';
import { EInvoicingSettingsSection } from '@/components/einvoicing/einvoicing-settings-section';
import { FeaturesSection } from '@/components/portal/features-section';
import { AiAccountantSettingsSection } from '@/components/ai-accountant/ai-settings-section';
import { GstRegistrationSection } from '@/components/gst/gst-settings-section';
import { Checkbox, Field, NativeSelect, Textarea } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { TextLink } from '@/components/menu-link';
import { SettingsSection } from '@/components/settings-section';
import {
  ArabicEmailSection,
  LanguageSettingsSection,
} from '@/components/language-settings-section';
import { toast } from '@/components/toast';
import { Input } from '@/components/ui/input';
import { formatTrn, useMe } from '@/lib/clients';
import { displayDate, utcOffsetLabel } from '@/lib/dates';
import { stateLabel } from '@/lib/gst';
import { i18n, useT } from '@/lib/i18n';
import { useOrgId } from '@/lib/org';
import {
  EMIRATES,
  MONTHS,
  TERMS,
  emirateLabel,
  monthName,
  termsLabel,
  useCompanySettings,
  useSaveSettings,
  type CompanySettings,
  type SettingsPatch,
} from '@/lib/settings';
import { cn } from '@/lib/utils';

const TABS = [
  { key: 'company' },
  { key: 'sales' },
  { key: 'expenses' },
  { key: 'advanced' },
] as const;
type Tab = (typeof TABS)[number]['key'];

export const Route = createFileRoute('/settings/company')({
  component: CompanySettingsPage,
  validateSearch: (s: Record<string, unknown>): { tab?: Tab } =>
    TABS.some((t) => t.key === s['tab']) ? { tab: s['tab'] as Tab } : {},
});

const TEMPLATES = ['trading', 'services', 'free_zone', 'india_trading', 'india_services'] as const;
/** The chart template a company started from, in the reader's language. */
const templateLabel = (key: string): string =>
  (TEMPLATES as readonly string[]).includes(key)
    ? i18n.t(`settingsPages.company.advancedTab.templates.${key as (typeof TEMPLATES)[number]}`)
    : key;
const yesNo = (b: boolean) => (b ? i18n.t('common.on') : i18n.t('common.off'));

const LOCATION_LABELS = [
  'Location',
  'Business',
  'Department',
  'Division',
  'Property',
  'Store',
  'Territory',
] as const;
/** A location label (stored in English) in the reader's language. */
const locationLabelText = (label: string): string =>
  (LOCATION_LABELS as readonly string[]).includes(label)
    ? i18n.t(
        `settingsPages.company.advancedTab.locationLabels.${label.toLowerCase() as Lowercase<(typeof LOCATION_LABELS)[number]>}`,
      )
    : label;
const CLASS_MODES = ['off', 'per_line', 'per_transaction'] as const;

/** Screen G1 (document 06): left tabs, sections with a read view and an inline pencil. */
function CompanySettingsPage() {
  const orgId = useOrgId();
  const { tab = 'company' } = Route.useSearch();
  const settings = useCompanySettings();
  const me = useMe();
  const canEdit = me.data?.isStaff ?? false;
  const t = useT();

  return (
    <>
      <PageHeader
        title={t('settingsPages.company.page.title')}
        description={settings.data ? settings.data.company.name : undefined}
      />
      {!orgId ? (
        <EmptyState
          title={t('shell.selectClient')}
          hint={t('settingsPages.company.page.selectClientHint')}
        />
      ) : settings.isPending ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : settings.isError ? (
        <EmptyState
          title={t('settingsPages.company.page.loadError')}
          hint={settings.error.message}
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-[180px_minmax(0,1fr)] md:gap-6">
          {/* a scrolling row of tabs on a phone, a side list from `md` */}
          <nav
            aria-label={t('settingsPages.company.page.tabsAria')}
            className="-mx-4 flex gap-1 overflow-x-auto border-b px-4 md:mx-0 md:flex-col md:border-b-0 md:px-0"
          >
            {TABS.map((x) => (
              <Link
                key={x.key}
                to="/settings/company"
                search={{ tab: x.key }}
                className={cn(
                  'shrink-0 whitespace-nowrap border-b-2 px-3 py-2 text-sm md:rounded-md md:border-b-0 md:border-s-2',
                  tab === x.key
                    ? 'border-primary bg-accent font-medium text-accent-foreground'
                    : 'border-transparent text-muted-foreground hover:bg-muted',
                )}
              >
                {t(`settingsPages.company.page.tabs.${x.key}`)}
              </Link>
            ))}
          </nav>
          <div className="flex min-w-0 max-w-4xl flex-col gap-4">
            {!canEdit ? (
              <p className="text-sm text-muted-foreground">
                {t('settingsPages.company.page.readOnlyNote')}
              </p>
            ) : null}
            {tab === 'company' ? <CompanyTab s={settings.data} canEdit={canEdit} /> : null}
            {tab === 'sales' ? <SalesTab s={settings.data} canEdit={canEdit} /> : null}
            {tab === 'expenses' ? <ExpensesTab s={settings.data} canEdit={canEdit} /> : null}
            {tab === 'advanced' ? <AdvancedTab s={settings.data} canEdit={canEdit} /> : null}
          </div>
        </div>
      )}
    </>
  );
}

/** Save a patch and toast; field errors come back from the server through SettingsSection. */
function useSave() {
  const save = useSaveSettings();
  /** `saved` is the toast after saving, such as "Address saved." */
  return async (patch: SettingsPatch, saved: string) => {
    await save.mutateAsync(patch);
    toast(saved);
  };
}

type Draft = Record<string, string | boolean>;
function useDraft(initial: () => Draft) {
  const [d, setD] = useState<Draft>({});
  return {
    d,
    load: () => setD(initial()),
    str: (k: string) => String(d[k] ?? ''),
    bool: (k: string) => Boolean(d[k]),
    set: (k: string) => (v: string | boolean) => setD((x) => ({ ...x, [k]: v })),
  };
}

function CompanyTab({ s, canEdit }: { s: CompanySettings; canEdit: boolean }) {
  const save = useSave();
  const t = useT();
  const c = s.company;
  const name = useDraft(() => ({ name: c.name, legalName: c.legalName ?? '' }));
  const contact = useDraft(() => ({
    email: c.email ?? '',
    phone: c.phone ?? '',
    website: c.website ?? '',
  }));
  const addr = useDraft(() => ({
    line1: c.address.line1 ?? '',
    line2: c.address.line2 ?? '',
    city: c.address.city ?? '',
    poBox: c.address.poBox ?? '',
    postalCode: c.address.postalCode ?? '',
    emirate: c.emirate ?? '',
  }));
  // India clients: PIN code and GST state instead of P.O. Box and emirate
  const india = Boolean(s.gst);
  const vat = useDraft(() => ({
    vatRegistered: s.vat.vatRegistered,
    trn: s.vat.trn ? formatTrn(s.vat.trn) : '',
    vatRegistrationDate: s.vat.vatRegistrationDate ?? '',
    taxPeriod: s.vat.taxPeriod,
  }));
  const address = (
    india
      ? [
          c.address.line1,
          c.address.line2,
          [c.address.city, c.address.postalCode].filter(Boolean).join(' '),
          t('settingsPages.company.companyTab.indiaLine', { state: stateLabel(s.gst?.stateCode) }),
        ]
      : [
          c.address.line1,
          c.address.line2,
          [...new Set([c.address.city, c.emirate].filter(Boolean))]
            .map((x) => (x === c.emirate ? emirateLabel(x) : x))
            .join(t('settingsPages.migrationTransactions.listSeparator')),
          c.address.poBox
            ? t('settingsPages.company.companyTab.poBoxLine', { poBox: c.address.poBox })
            : null,
          t('settingsPages.company.companyTab.uae'),
        ]
  )
    .filter(Boolean)
    .join('\n');

  return (
    <>
      <SettingsSection
        title={t('settingsPages.company.companyTab.nameSection')}
        testId="section-company-name"
        canEdit={canEdit}
        onEdit={name.load}
        rows={[
          [t('settingsPages.company.companyTab.companyName'), c.name],
          [t('settingsPages.company.companyTab.legalName'), c.legalName],
          [t('settingsPages.company.companyTab.logo'), <LogoPlaceholder key="logo" />],
        ]}
        onSave={async () => {
          if (name.str('name').trim().length < 2)
            return { name: t('settingsPages.company.companyTab.nameRequired') };
          await save(
            { company: { name: name.str('name'), legalName: name.str('legalName') } },
            t('settingsPages.company.companyTab.nameSaved'),
          );
        }}
      >
        {(errors) => (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field
              label={t('settingsPages.company.companyTab.companyName')}
              hint={t('settingsPages.company.companyTab.nameHint')}
              error={errors['name']}
            >
              {(id) => (
                <Input
                  id={id}
                  value={name.str('name')}
                  maxLength={200}
                  aria-invalid={Boolean(errors['name'])}
                  onChange={(e) => name.set('name')(e.target.value)}
                  autoFocus
                />
              )}
            </Field>
            <Field
              label={t('settingsPages.company.companyTab.legalName')}
              hint={t('settingsPages.company.companyTab.legalNameHint')}
            >
              {(id) => (
                <Input
                  id={id}
                  value={name.str('legalName')}
                  maxLength={200}
                  onChange={(e) => name.set('legalName')(e.target.value)}
                />
              )}
            </Field>
          </div>
        )}
      </SettingsSection>

      <LanguageSettingsSection s={s} canEdit={canEdit} />

      <SettingsSection
        title={t('settingsPages.company.companyTab.contactSection')}
        testId="section-contact"
        canEdit={canEdit}
        onEdit={contact.load}
        rows={[
          [t('settingsPages.company.companyTab.companyEmail'), c.email],
          [t('settingsPages.company.companyTab.companyPhone'), c.phone],
          [t('settingsPages.company.companyTab.website'), c.website],
        ]}
        onSave={async () => {
          const email = contact.str('email').trim();
          if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email))
            return { email: t('settingsPages.company.companyTab.invalidEmail') };
          await save(
            {
              company: {
                email,
                phone: contact.str('phone'),
                website: contact.str('website'),
              },
            },
            t('settingsPages.company.companyTab.contactSaved'),
          );
        }}
      >
        {(errors) => (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Field
              label={t('settingsPages.company.companyTab.companyEmail')}
              error={errors['email']}
            >
              {(id) => (
                <Input
                  id={id}
                  type="email"
                  value={contact.str('email')}
                  maxLength={200}
                  aria-invalid={Boolean(errors['email'])}
                  onChange={(e) => contact.set('email')(e.target.value)}
                />
              )}
            </Field>
            <Field label={t('settingsPages.company.companyTab.companyPhone')}>
              {(id) => (
                <Input
                  id={id}
                  value={contact.str('phone')}
                  maxLength={40}
                  onChange={(e) => contact.set('phone')(e.target.value)}
                />
              )}
            </Field>
            <Field label={t('settingsPages.company.companyTab.website')}>
              {(id) => (
                <Input
                  id={id}
                  value={contact.str('website')}
                  maxLength={200}
                  onChange={(e) => contact.set('website')(e.target.value)}
                />
              )}
            </Field>
          </div>
        )}
      </SettingsSection>

      <SettingsSection
        title={t('settingsPages.company.companyTab.addressSection')}
        testId="section-address"
        canEdit={canEdit}
        onEdit={addr.load}
        rows={[[t('settingsPages.company.companyTab.companyAddress'), address]]}
        onSave={() =>
          save(
            {
              company: {
                address: {
                  line1: addr.str('line1'),
                  line2: addr.str('line2'),
                  city: addr.str('city'),
                  ...(india
                    ? { postalCode: addr.str('postalCode') }
                    : { poBox: addr.str('poBox') }),
                },
                ...(india ? {} : { emirate: addr.str('emirate') || null }),
              },
            },
            t('settingsPages.company.companyTab.addressSaved'),
          )
        }
      >
        {() => (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field
              label={t('settingsPages.company.companyTab.streetAddress')}
              className="col-span-2"
            >
              {(id) => (
                <Input
                  id={id}
                  value={addr.str('line1')}
                  maxLength={200}
                  onChange={(e) => addr.set('line1')(e.target.value)}
                />
              )}
            </Field>
            <Field
              label={t('settingsPages.company.companyTab.addressLine2')}
              className="col-span-2"
            >
              {(id) => (
                <Input
                  id={id}
                  value={addr.str('line2')}
                  maxLength={200}
                  onChange={(e) => addr.set('line2')(e.target.value)}
                />
              )}
            </Field>
            <Field label={t('settingsPages.company.companyTab.city')}>
              {(id) => (
                <Input
                  id={id}
                  value={addr.str('city')}
                  maxLength={100}
                  onChange={(e) => addr.set('city')(e.target.value)}
                />
              )}
            </Field>
            {india ? (
              <>
                <Field label={t('settingsPages.company.companyTab.pinCode')}>
                  {(id) => (
                    <Input
                      id={id}
                      value={addr.str('postalCode')}
                      maxLength={12}
                      inputMode="numeric"
                      onChange={(e) => addr.set('postalCode')(e.target.value)}
                    />
                  )}
                </Field>
                <Field
                  label={t('settingsPages.company.companyTab.state')}
                  hint={t('settingsPages.company.companyTab.stateHint')}
                >
                  {(id) => <Input id={id} value={stateLabel(s.gst?.stateCode)} disabled />}
                </Field>
                <Field label={t('settingsPages.company.companyTab.country')}>
                  {(id) => (
                    <Input id={id} value={t('settingsPages.company.companyTab.india')} disabled />
                  )}
                </Field>
              </>
            ) : null}
            {india ? null : (
              <Field label={t('settingsPages.company.companyTab.poBox')}>
                {(id) => (
                  <Input
                    id={id}
                    value={addr.str('poBox')}
                    maxLength={20}
                    onChange={(e) => addr.set('poBox')(e.target.value)}
                  />
                )}
              </Field>
            )}
            {india ? null : (
              <Field
                label={t('settingsPages.company.companyTab.emirate')}
                hint={t('settingsPages.company.companyTab.emirateHint')}
              >
                {(id) => (
                  <NativeSelect
                    id={id}
                    value={addr.str('emirate')}
                    onChange={(e) => addr.set('emirate')(e.target.value)}
                  >
                    <option value="">{t('settingsPages.company.companyTab.notSet')}</option>
                    {EMIRATES.map((em) => (
                      <option key={em} value={em}>
                        {emirateLabel(em)}
                      </option>
                    ))}
                  </NativeSelect>
                )}
              </Field>
            )}
            {india ? null : (
              <Field label={t('settingsPages.company.companyTab.country')}>
                {(id) => (
                  <Input id={id} value={t('settingsPages.company.companyTab.uae')} disabled />
                )}
              </Field>
            )}
          </div>
        )}
      </SettingsSection>

      {india ? (
        <GstRegistrationSection s={s} canEdit={canEdit} />
      ) : (
        <SettingsSection
          title={t('settingsPages.company.companyTab.vatSection')}
          testId="section-vat"
          canEdit={canEdit}
          onEdit={vat.load}
          rows={[
            [
              t('settingsPages.company.companyTab.vatRegistered'),
              s.vat.vatRegistered ? t('common.yes') : t('common.no'),
            ],
            [
              t('settingsPages.company.companyTab.trn'),
              s.vat.trnPending
                ? t('settingsPages.company.companyTab.pending')
                : formatTrn(s.vat.trn),
            ],
            [
              t('settingsPages.company.companyTab.effectiveDate'),
              s.vat.vatRegistrationDate ? displayDate(s.vat.vatRegistrationDate) : '',
            ],
            [
              t('settingsPages.company.companyTab.filingPeriod'),
              s.vat.taxPeriod === 'monthly'
                ? t('settingsPages.company.companyTab.monthly')
                : t('settingsPages.company.companyTab.quarterly'),
            ],
            [
              t('settingsPages.company.companyTab.taxPack'),
              s.vat.taxPack === 'uae'
                ? t('settingsPages.company.companyTab.taxPackUae')
                : s.vat.taxPack,
            ],
          ]}
          onSave={async () => {
            const trn = vat.str('trn').replace(/\s+/g, '');
            if (trn && !/^[0-9]{15}$/.test(trn))
              return { trn: t('settingsPages.company.companyTab.trnDigits') };
            if (vat.bool('vatRegistered') && !trn && !s.vat.trnPending)
              return { trn: t('settingsPages.company.companyTab.trnRequired') };
            await save(
              {
                vat: {
                  vatRegistered: vat.bool('vatRegistered'),
                  trn: trn || null,
                  vatRegistrationDate: vat.str('vatRegistrationDate') || null,
                  taxPeriod: vat.str('taxPeriod'),
                },
              },
              t('settingsPages.company.companyTab.vatSaved'),
            );
          }}
        >
          {(errors) => (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Checkbox
                className="col-span-2"
                label={t('settingsPages.company.companyTab.registeredForVat')}
                checked={vat.bool('vatRegistered')}
                onChange={(e) => vat.set('vatRegistered')(e.target.checked)}
              />
              <Field
                label={t('settingsPages.company.companyTab.trn')}
                hint={t('settingsPages.company.companyTab.trnHint')}
                error={errors['trn']}
              >
                {(id) => (
                  <Input
                    id={id}
                    value={vat.str('trn')}
                    inputMode="numeric"
                    placeholder="100 1234567 00 003"
                    aria-invalid={Boolean(errors['trn'])}
                    onChange={(e) => vat.set('trn')(e.target.value)}
                  />
                )}
              </Field>
              <Field label={t('settingsPages.company.companyTab.effectiveDate')}>
                {(id) => (
                  <DateInput
                    id={id}
                    value={vat.str('vatRegistrationDate')}
                    onChange={(v) => vat.set('vatRegistrationDate')(v)}
                  />
                )}
              </Field>
              <Field label={t('settingsPages.company.companyTab.filingPeriod')}>
                {(id) => (
                  <NativeSelect
                    id={id}
                    value={vat.str('taxPeriod')}
                    onChange={(e) => vat.set('taxPeriod')(e.target.value)}
                  >
                    <option value="quarterly">
                      {t('settingsPages.company.companyTab.quarterly')}
                    </option>
                    <option value="monthly">{t('settingsPages.company.companyTab.monthly')}</option>
                  </NativeSelect>
                )}
              </Field>
            </div>
          )}
        </SettingsSection>
      )}
    </>
  );
}

function LogoPlaceholder() {
  const t = useT();
  return (
    <span className="inline-flex items-center gap-3">
      <span className="flex size-12 items-center justify-center rounded-md border border-dashed text-muted-foreground">
        <ImageIcon className="size-5" aria-hidden />
      </span>
      <span className="text-xs text-muted-foreground">
        {t('settingsPages.company.companyTab.logoSoon')}
      </span>
    </span>
  );
}

function TermsSelect({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <NativeSelect id={id} value={value} onChange={(e) => onChange(e.target.value)}>
      {TERMS.map((x) => (
        <option key={x.days} value={x.days}>
          {x.label}
        </option>
      ))}
    </NativeSelect>
  );
}

function SalesTab({ s, canEdit }: { s: CompanySettings; canEdit: boolean }) {
  const save = useSave();
  const t = useT();
  const form = useDraft(() => ({
    defaultTermsDays: String(s.sales.defaultTermsDays),
    invoicePrefix: s.sales.invoicePrefix,
    showSku: s.sales.showSku,
  }));
  const msg = useDraft(() => ({
    invoiceMessage: s.sales.invoiceMessage ?? '',
    statementMessage: s.sales.statementMessage ?? '',
  }));
  const mail = useDraft(() => ({
    emailSubject: s.sales.emailSubject,
    emailMessage: s.sales.emailMessage,
    emailCc: s.sales.emailCc ?? '',
  }));
  return (
    <>
      <SettingsSection
        title={t('settingsPages.company.salesTab.formSection')}
        testId="section-sales-form"
        canEdit={canEdit}
        onEdit={form.load}
        rows={[
          [
            t('settingsPages.company.salesTab.preferredTerms'),
            termsLabel(s.sales.defaultTermsDays),
          ],
          [t('settingsPages.company.salesTab.invoicePrefix'), s.sales.invoicePrefix],
          [t('settingsPages.company.salesTab.showSku'), yesNo(s.sales.showSku)],
        ]}
        onSave={async () => {
          if (!/^[A-Za-z0-9-]{0,10}$/.test(form.str('invoicePrefix')))
            return { invoicePrefix: t('settingsPages.company.salesTab.prefixInvalid') };
          await save(
            {
              sales: {
                defaultTermsDays: Number(form.str('defaultTermsDays')),
                invoicePrefix: form.str('invoicePrefix'),
                showSku: form.bool('showSku'),
              },
            },
            t('settingsPages.company.salesTab.formSaved'),
          );
        }}
      >
        {(errors) => (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label={t('settingsPages.company.salesTab.preferredTerms')}>
              {(id) => (
                <TermsSelect
                  id={id}
                  value={form.str('defaultTermsDays')}
                  onChange={form.set('defaultTermsDays')}
                />
              )}
            </Field>
            <Field
              label={t('settingsPages.company.salesTab.invoicePrefix')}
              hint={t('settingsPages.company.salesTab.prefixHint')}
              error={errors['invoicePrefix']}
            >
              {(id) => (
                <Input
                  id={id}
                  value={form.str('invoicePrefix')}
                  maxLength={10}
                  aria-invalid={Boolean(errors['invoicePrefix'])}
                  onChange={(e) => form.set('invoicePrefix')(e.target.value)}
                />
              )}
            </Field>
            <Checkbox
              label={t('settingsPages.company.salesTab.showSkuOnForms')}
              checked={form.bool('showSku')}
              onChange={(e) => form.set('showSku')(e.target.checked)}
            />
          </div>
        )}
      </SettingsSection>
      <SettingsSection
        title={t('settingsPages.company.salesTab.messagesSection')}
        testId="section-messages"
        canEdit={canEdit}
        onEdit={msg.load}
        rows={[
          [t('settingsPages.company.salesTab.invoiceMessage'), s.sales.invoiceMessage],
          [t('settingsPages.company.salesTab.statementMessage'), s.sales.statementMessage],
        ]}
        onSave={() =>
          save(
            {
              sales: {
                invoiceMessage: msg.str('invoiceMessage'),
                statementMessage: msg.str('statementMessage'),
              },
            },
            t('settingsPages.company.salesTab.messagesSaved'),
          )
        }
      >
        {() => (
          <>
            <Field label={t('settingsPages.company.salesTab.invoiceMessage')}>
              {(id) => (
                <Textarea
                  id={id}
                  value={msg.str('invoiceMessage')}
                  maxLength={1000}
                  onChange={(e) => msg.set('invoiceMessage')(e.target.value)}
                />
              )}
            </Field>
            <Field label={t('settingsPages.company.salesTab.statementMessage')}>
              {(id) => (
                <Textarea
                  id={id}
                  value={msg.str('statementMessage')}
                  maxLength={1000}
                  onChange={(e) => msg.set('statementMessage')(e.target.value)}
                />
              )}
            </Field>
          </>
        )}
      </SettingsSection>
      <ArabicEmailSection s={s} canEdit={canEdit} />
      <SettingsSection
        title={t('settingsPages.company.salesTab.emailSection')}
        testId="section-email-messages"
        canEdit={canEdit}
        onEdit={mail.load}
        rows={[
          [t('settingsPages.company.salesTab.emailSubject'), s.sales.emailSubject],
          [t('settingsPages.company.salesTab.emailMessage'), s.sales.emailMessage],
          [t('settingsPages.company.salesTab.emailCc'), s.sales.emailCc],
        ]}
        onSave={async () => {
          const errors: Record<string, string> = {};
          if (!mail.str('emailSubject').trim())
            errors['emailSubject'] = t('settingsPages.company.salesTab.subjectRequired');
          if (!mail.str('emailMessage').trim())
            errors['emailMessage'] = t('settingsPages.company.salesTab.messageRequired');
          const cc = mail.str('emailCc').trim();
          if (cc && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cc))
            errors['emailCc'] = t('settingsPages.company.salesTab.invalidEmail');
          if (Object.keys(errors).length) return errors;
          await save(
            {
              sales: {
                emailSubject: mail.str('emailSubject'),
                emailMessage: mail.str('emailMessage'),
                emailCc: cc,
              },
            },
            t('settingsPages.company.salesTab.emailSaved'),
          );
        }}
      >
        {(errors) => (
          <>
            <p className="text-sm text-muted-foreground">
              {t('settingsPages.company.salesTab.emailHelp')}
            </p>
            <Field
              label={t('settingsPages.company.salesTab.emailSubject')}
              error={errors['emailSubject']}
            >
              {(id) => (
                <Input
                  id={id}
                  value={mail.str('emailSubject')}
                  maxLength={300}
                  aria-invalid={Boolean(errors['emailSubject'])}
                  onChange={(e) => mail.set('emailSubject')(e.target.value)}
                />
              )}
            </Field>
            <Field
              label={t('settingsPages.company.salesTab.emailMessage')}
              error={errors['emailMessage']}
            >
              {(id) => (
                <Textarea
                  id={id}
                  className="min-h-40"
                  value={mail.str('emailMessage')}
                  maxLength={5000}
                  aria-invalid={Boolean(errors['emailMessage'])}
                  onChange={(e) => mail.set('emailMessage')(e.target.value)}
                />
              )}
            </Field>
            <Field
              label={t('settingsPages.company.salesTab.emailCc')}
              hint={t('settingsPages.company.salesTab.emailCcHint')}
              error={errors['emailCc']}
            >
              {(id) => (
                <Input
                  id={id}
                  value={mail.str('emailCc')}
                  maxLength={200}
                  aria-invalid={Boolean(errors['emailCc'])}
                  onChange={(e) => mail.set('emailCc')(e.target.value)}
                />
              )}
            </Field>
          </>
        )}
      </SettingsSection>
      {s.gst ? null : <EInvoicingSettingsSection canEdit={canEdit} />}
    </>
  );
}

function ExpensesTab({ s, canEdit }: { s: CompanySettings; canEdit: boolean }) {
  const save = useSave();
  const t = useT();
  const form = useDraft(() => ({
    defaultBillTermsDays: String(s.expenses.defaultBillTermsDays),
    showItemsTable: s.expenses.showItemsTable,
  }));
  return (
    <SettingsSection
      title={t('settingsPages.company.expensesTab.section')}
      testId="section-bills"
      canEdit={canEdit}
      onEdit={form.load}
      rows={[
        [
          t('settingsPages.company.expensesTab.billTerms'),
          termsLabel(s.expenses.defaultBillTermsDays),
        ],
        [t('settingsPages.company.expensesTab.showItems'), yesNo(s.expenses.showItemsTable)],
      ]}
      onSave={() =>
        save(
          {
            expenses: {
              defaultBillTermsDays: Number(form.str('defaultBillTermsDays')),
              showItemsTable: form.bool('showItemsTable'),
            },
          },
          t('settingsPages.company.expensesTab.saved'),
        )
      }
    >
      {() => (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label={t('settingsPages.company.expensesTab.billTerms')}>
            {(id) => (
              <TermsSelect
                id={id}
                value={form.str('defaultBillTermsDays')}
                onChange={form.set('defaultBillTermsDays')}
              />
            )}
          </Field>
          <Checkbox
            label={t('settingsPages.company.expensesTab.showItems')}
            checked={form.bool('showItemsTable')}
            onChange={(e) => form.set('showItemsTable')(e.target.checked)}
          />
        </div>
      )}
    </SettingsSection>
  );
}

function AdvancedTab({ s, canEdit }: { s: CompanySettings; canEdit: boolean }) {
  const save = useSave();
  const t = useT();
  const a = s.advanced;
  const acct = useDraft(() => ({ fiscalYearStart: String(a.fiscalYearStart) }));
  const coa = useDraft(() => ({ enableAccountNumbers: a.enableAccountNumbers }));
  const other = useDraft(() => ({
    warnDuplicateInvoiceNumbers: a.warnDuplicateInvoiceNumbers,
    warnDuplicateBillNumbers: a.warnDuplicateBillNumbers,
  }));
  const cats = useDraft(() => ({
    trackClasses: a.trackClasses ?? 'off',
    trackLocations: a.trackLocations ?? false,
    locationLabel: a.locationLabel ?? 'Location',
    useProjects: a.useProjects ?? false,
  }));
  const classMode = (k: string) =>
    (CLASS_MODES as readonly string[]).includes(k)
      ? t(`settingsPages.company.advancedTab.classModes.${k as (typeof CLASS_MODES)[number]}`)
      : undefined;
  return (
    <>
      <SettingsSection
        title={t('settingsPages.company.advancedTab.accountingSection')}
        testId="section-accounting"
        canEdit={canEdit}
        onEdit={acct.load}
        rows={[
          [t('settingsPages.company.advancedTab.firstMonth'), monthName(a.fiscalYearStart)],
          [
            t('settingsPages.company.advancedTab.closeBooks'),
            <span key="close">
              <Trans
                t={t}
                i18nKey="settingsPages.companyRows.closeBooks"
                components={{ lnk: <TextLink to="/accounting/periods">{null}</TextLink> }}
              />
            </span>,
          ],
        ]}
        onSave={() =>
          save(
            { advanced: { fiscalYearStart: Number(acct.str('fiscalYearStart')) } },
            t('settingsPages.company.advancedTab.accountingSaved'),
          )
        }
      >
        {() => (
          <Field
            label={t('settingsPages.company.advancedTab.firstMonth')}
            hint={
              a.canChangeFiscalYearStart
                ? t('settingsPages.company.advancedTab.firstMonthChangeable')
                : t('settingsPages.company.advancedTab.firstMonthFixed')
            }
          >
            {(id) => (
              <NativeSelect
                id={id}
                className="max-w-xs"
                value={acct.str('fiscalYearStart')}
                disabled={!a.canChangeFiscalYearStart}
                onChange={(e) => acct.set('fiscalYearStart')(e.target.value)}
              >
                {MONTHS.map((m, i) => (
                  <option key={m} value={i + 1}>
                    {monthName(i + 1)}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
        )}
      </SettingsSection>
      <SettingsSection
        title={t('settingsPages.company.advancedTab.coaSection')}
        testId="section-coa"
        canEdit={canEdit}
        onEdit={coa.load}
        rows={[
          [
            t('settingsPages.company.advancedTab.enableAccountNumbers'),
            yesNo(a.enableAccountNumbers),
          ],
          [
            t('settingsPages.company.advancedTab.chartTemplate'),
            a.chartTemplate ? templateLabel(a.chartTemplate) : '',
          ],
        ]}
        onSave={() =>
          save(
            { advanced: { enableAccountNumbers: coa.bool('enableAccountNumbers') } },
            t('settingsPages.company.advancedTab.coaSaved'),
          )
        }
      >
        {() => (
          <Checkbox
            label={t('settingsPages.company.advancedTab.enableAccountNumbers')}
            checked={coa.bool('enableAccountNumbers')}
            onChange={(e) => coa.set('enableAccountNumbers')(e.target.checked)}
          />
        )}
      </SettingsSection>
      <SettingsSection
        title={t('settingsPages.company.advancedTab.categoriesSection')}
        testId="section-categories"
        canEdit={canEdit}
        onEdit={cats.load}
        rows={[
          [t('settingsPages.company.advancedTab.trackClasses'), classMode(a.trackClasses ?? 'off')],
          [
            t('settingsPages.company.advancedTab.trackLocations'),
            a.trackLocations
              ? t('settingsPages.company.advancedTab.locationsOn', {
                  label: locationLabelText(a.locationLabel ?? 'Location'),
                })
              : t('common.off'),
          ],
          [t('settingsPages.company.advancedTab.projects'), yesNo(a.useProjects ?? false)],
        ]}
        onSave={() =>
          save(
            {
              advanced: {
                trackClasses: cats.str('trackClasses'),
                trackLocations: cats.bool('trackLocations'),
                locationLabel: cats.str('locationLabel'),
                useProjects: cats.bool('useProjects'),
              },
            },
            t('settingsPages.company.advancedTab.categoriesSaved'),
          )
        }
      >
        {() => (
          <div className="flex flex-col gap-3">
            <Field
              label={t('settingsPages.company.advancedTab.trackClasses')}
              hint={t('settingsPages.company.advancedTab.classesHint')}
            >
              {(id) => (
                <NativeSelect
                  id={id}
                  className="max-w-xs"
                  value={cats.str('trackClasses')}
                  onChange={(e) => cats.set('trackClasses')(e.target.value)}
                >
                  {CLASS_MODES.map((k) => (
                    <option key={k} value={k}>
                      {classMode(k)}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
            <Checkbox
              label={t('settingsPages.company.advancedTab.trackLocationsCheckbox')}
              checked={cats.bool('trackLocations')}
              onChange={(e) => cats.set('trackLocations')(e.target.checked)}
            />
            {cats.bool('trackLocations') ? (
              <Field label={t('settingsPages.company.advancedTab.locationLabel')}>
                {(id) => (
                  <NativeSelect
                    id={id}
                    className="max-w-xs"
                    value={cats.str('locationLabel')}
                    onChange={(e) => cats.set('locationLabel')(e.target.value)}
                  >
                    {LOCATION_LABELS.map((l) => (
                      <option key={l} value={l}>
                        {locationLabelText(l)}
                      </option>
                    ))}
                  </NativeSelect>
                )}
              </Field>
            ) : null}
            <Checkbox
              label={t('settingsPages.company.advancedTab.useProjects')}
              checked={cats.bool('useProjects')}
              onChange={(e) => cats.set('useProjects')(e.target.checked)}
            />
          </div>
        )}
      </SettingsSection>
      <CurrencySettingsSection canEdit={canEdit} home={a.baseCurrency} />
      <SettingsSection
        title={t('settingsPages.company.advancedTab.otherSection')}
        testId="section-other"
        canEdit={canEdit}
        onEdit={other.load}
        rows={[
          [t('settingsPages.company.advancedTab.dateFormat'), a.dateFormat],
          [t('settingsPages.company.advancedTab.numberFormat'), '1,234.56'],
          [
            t('settingsPages.company.advancedTab.timeZone'),
            utcOffsetLabel(a.timeZone)
              ? `${a.timeZone} (${utcOffsetLabel(a.timeZone)})`
              : a.timeZone,
          ],
          [
            t('settingsPages.company.advancedTab.warnInvoice'),
            yesNo(a.warnDuplicateInvoiceNumbers),
          ],
          [t('settingsPages.company.advancedTab.warnBill'), yesNo(a.warnDuplicateBillNumbers)],
        ]}
        onSave={() =>
          save(
            {
              advanced: {
                warnDuplicateInvoiceNumbers: other.bool('warnDuplicateInvoiceNumbers'),
                warnDuplicateBillNumbers: other.bool('warnDuplicateBillNumbers'),
              },
            },
            t('settingsPages.company.advancedTab.otherSaved'),
          )
        }
      >
        {() => (
          <div className="flex flex-col gap-2">
            <Checkbox
              label={t('settingsPages.company.advancedTab.warnInvoice')}
              checked={other.bool('warnDuplicateInvoiceNumbers')}
              onChange={(e) => other.set('warnDuplicateInvoiceNumbers')(e.target.checked)}
            />
            <Checkbox
              label={t('settingsPages.company.advancedTab.warnBill')}
              checked={other.bool('warnDuplicateBillNumbers')}
              onChange={(e) => other.set('warnDuplicateBillNumbers')(e.target.checked)}
            />
          </div>
        )}
      </SettingsSection>
      <FeaturesSection canEdit={canEdit} />
      <AiAccountantSettingsSection canEdit={canEdit} />
    </>
  );
}
